// The same five methods Supabase's supabase.auth used to expose, so
// AuthContext never had to change its shape when this replaced it -- our
// own /api/auth/* endpoints, via apps/api's local JWT auth.
import { authCookieStorage } from "./authStorage";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const KEY = "wa.session";

const listeners = new Set();

function read() {
  try {
    return JSON.parse(authCookieStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

function write(session) {
  if (session) authCookieStorage.setItem(KEY, JSON.stringify(session));
  else authCookieStorage.removeItem(KEY);
  listeners.forEach((cb) => cb(session ? "SIGNED_IN" : "SIGNED_OUT", session));
}

// Thrown only when the server actually answered and said no (a genuinely
// invalid/expired/revoked token) -- distinct from fetch() itself throwing
// (offline, DNS failure, a dropped connection), which is not a rejection
// and must not be treated as one.
class AuthRejectedError extends Error {}

async function post(path, body) {
  const res = await fetch(`${BASE}/api/auth/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new AuthRejectedError(data.detail || "Authentication failed");
  return data;
}

function toSession(tokens) {
  return {
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + (tokens.expires_in || 1800),
    // AuthContext's cache-clearing guard (never let one account's cached
    // rows show up for the next login on this browser) keys off
    // session.user.id specifically -- the same shape a Supabase session
    // carries. /api/auth/login|refresh return user_id for exactly this.
    user: { id: tokens.user_id },
  };
}

// Shared by every concurrent refreshSession() caller, so a burst of
// requests hitting the interceptor at once (several React Query queries
// firing after the tab was idle, or several tabs open) all await the same
// network round-trip instead of each rotating the one stored refresh
// token -- every caller but the first would otherwise see their copy
// already revoked (the rotation in Task 10) and log the user out.
let refreshInFlight = null;

async function doRefresh(refreshToken) {
  try {
    const session = toSession(await post("refresh", { refresh_token: refreshToken }));
    write(session);
    return { data: { session }, error: null };
  } catch (error) {
    // Only an explicit rejection means this refresh token is actually
    // dead -- a network error (offline, a dropped connection) must not
    // log the user out just because this one request didn't reach the
    // server; the existing session is still good and worth retrying.
    if (error instanceof AuthRejectedError) write(null);
    return { data: { session: null }, error };
  } finally {
    refreshInFlight = null;
  }
}

const local = {
  async signInWithPassword({ email, password }) {
    try {
      write(toSession(await post("login", { email, password })));
      return { error: null };
    } catch (error) {
      return { error };
    }
  },
  async getSession() {
    return { data: { session: read() } };
  },
  async refreshSession() {
    if (refreshInFlight) return refreshInFlight;
    const current = read();
    if (!current?.refresh_token) return { data: { session: null }, error: new Error("No session") };
    refreshInFlight = doRefresh(current.refresh_token);
    return refreshInFlight;
  },
  async signOut() {
    const current = read();
    if (current?.refresh_token) {
      await post("logout", { refresh_token: current.refresh_token }).catch(() => {});
    }
    write(null);
    return { error: null };
  },
  onAuthStateChange(cb) {
    listeners.add(cb);
    return { data: { subscription: { unsubscribe: () => listeners.delete(cb) } } };
  },
};

export const authBackend = local;
